import { Component, Input, computed, inject, signal, OnInit } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import {
  Booking,
  SECURITY_DEPOSIT_METHOD_LABELS,
  SecurityDepositMethod,
} from '../../models/booking.model';
import { BookingService } from '../../services/booking.service';
import { OwnerSettings } from '../../models/user.model';

const RECEIPT_METHODS = new Set<SecurityDepositMethod>(['transfer', 'bizum']);

@Component({
  selector: 'app-guest-security-deposit-panel',
  standalone: true,
  imports: [LucideAngularModule, DecimalPipe],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './guest-security-deposit-panel.component.html',
})
export class GuestSecurityDepositPanelComponent implements OnInit {
  @Input({ required: true }) booking!: Booking;

  private bookingService = inject(BookingService);

  readonly selectedMethod = signal<SecurityDepositMethod | null>(null);
  readonly receiptFile = signal<File | null>(null);
  readonly receiptFileName = signal('');
  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);
  readonly loadingOptions = signal(true);
  readonly ownerSettings = signal<Partial<OwnerSettings> | null>(null);
  readonly cardHoldAvailable = signal(false);

  readonly depositEur = computed(() => (this.booking.depositCents ?? 0) / 100);

  /** Uses the owner's current Ajustes, not the snapshot from checkout. */
  readonly availableMethods = computed((): SecurityDepositMethod[] => {
    const s = this.ownerSettings();
    if (!s) return [];
    const methods: SecurityDepositMethod[] = [];
    if (s.sepaEnabled) methods.push('transfer');
    if (s.bizumEnabled) methods.push('bizum');
    if (s.cashEnabled) methods.push('cash');
    if (this.cardHoldAvailable()) methods.push('card_hold');
    return methods;
  });

  readonly ownerPayment = computed(() => this.ownerSettings());

  readonly methodLabel = (method: SecurityDepositMethod) =>
    SECURITY_DEPOSIT_METHOD_LABELS[method];

  readonly canSubmit = computed(() => {
    const m = this.selectedMethod();
    if (!m || this.submitting()) return false;
    if (m === 'card_hold' || m === 'cash') return true;
    if (RECEIPT_METHODS.has(m)) return !!this.receiptFile();
    return false;
  });

  ngOnInit(): void {
    void this.loadPaymentOptions();
  }

  retryLoad(): void {
    this.error.set(null);
    void this.loadPaymentOptions();
  }

  private async loadPaymentOptions(): Promise<void> {
    const bookingId = this.booking.id;
    if (!bookingId) {
      this.loadingOptions.set(false);
      return;
    }
    this.loadingOptions.set(true);
    try {
      const details = await this.bookingService.fetchOwnerPaymentDetails(bookingId);
      this.ownerSettings.set({
        accountHolder: details.accountHolder,
        iban: details.iban,
        mobilePhone: details.mobilePhone,
        sepaEnabled: details.sepaEnabled,
        bizumEnabled: details.bizumEnabled,
        cashEnabled: details.cashEnabled,
      });
      this.cardHoldAvailable.set(!!details.cardHoldAvailable);
    } catch {
      this.error.set('No se pudieron cargar los métodos de pago del propietario.');
    } finally {
      this.loadingOptions.set(false);
    }
  }

  selectMethod(method: SecurityDepositMethod): void {
    this.selectedMethod.set(method);
    this.receiptFile.set(null);
    this.receiptFileName.set('');
    this.error.set(null);
  }

  onReceiptSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    this.receiptFile.set(file);
    this.receiptFileName.set(file?.name ?? '');
  }

  triggerReceiptInput(input: HTMLInputElement): void {
    input.click();
  }

  formatIban(iban?: string): string {
    return (iban ?? '').replace(/\s+/g, ' ').trim();
  }

  async submit(): Promise<void> {
    const method = this.selectedMethod();
    const bookingId = this.booking.id;
    if (!method || !bookingId || !this.canSubmit()) return;

    this.submitting.set(true);
    this.error.set(null);

    try {
      if (method === 'card_hold') {
        const { url } = await this.bookingService.createDepositCardHoldSession(bookingId);
        window.location.href = url;
        return;
      }

      let receiptUrl: string | undefined;
      if (RECEIPT_METHODS.has(method)) {
        const file = this.receiptFile();
        if (!file) return;
        receiptUrl = await this.bookingService.uploadSecurityDepositReceipt(bookingId, file);
      }

      await this.bookingService.submitSecurityDeposit({
        bookingId,
        method,
        ...(receiptUrl ? { securityDepositReceiptUrl: receiptUrl } : {}),
      });
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message
        ?? 'No se pudo enviar la fianza. Inténtalo de nuevo.';
      this.error.set(msg);
    } finally {
      this.submitting.set(false);
    }
  }
}
